// Handrolled - no openApi for content
import { HttpNoServerResponse } from '@/core/http/HttpNoServerResponse';
import { HttpServerError } from '@/core/http/HttpServerError';
import { SiteSettingsViewModel, PageData } from '@/api/content/models';
import { cmsHost, siteSettings, firstPage } from '@/core/infrastructure/environment';
import { extractTwoLetterCultureFromPath } from '@/project/intl/useCulture';
import axios from 'axios';
import applicationVersionInterceptor from '@/core/http/interceptors/applicationVersion.interceptor';

const axiosInstance = axios.create({
    baseURL: cmsHost,
    params: {
        xhr: 1,
    },
});
axiosInstance.interceptors.response.use(applicationVersionInterceptor);

const originalPath = location.pathname;

let hasInitialPageData = !!firstPage;
async function getPage(path: string): Promise<PageData> {
    // Take from appData.firstPage if present (production), first time on path
    const useInitialPageData = hasInitialPageData && path === originalPath;
    if (useInitialPageData) {
        hasInitialPageData = false;
        return Promise.resolve(firstPage);
    }

    try {
        const response = await axiosInstance.get(path);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            if (error.response) {
                throw new HttpServerError(error.response);
            } else {
                throw new HttpNoServerResponse();
            }
        } else {
            throw error;
        }
    }
}

async function loadSiteSettings(urlCulture: string | null): Promise<SiteSettingsViewModel> {

    // Take from appData when culture matches current URL.
    if (siteSettings) {
        const settingsCulture = (siteSettings.culture as string | undefined)?.toLowerCase().slice(0, 2);
        if (!urlCulture || !settingsCulture || settingsCulture === urlCulture) {
            return Promise.resolve(siteSettings);
        }
    }

    const settingsPath = urlCulture ? `/${urlCulture}/settings` : '/settings';
    try {
        const settingsPage = await getPage(settingsPath);
        return settingsPage.content as SiteSettingsViewModel;
    } catch {
        // Fallback for markets where localized settings path is unavailable.
        const settingsPage = await getPage('/settings');
        return settingsPage.content as SiteSettingsViewModel;
    }
}

const settingsCache = new Map<string, Promise<SiteSettingsViewModel>>();

function getSettingsCacheKey(): string {
    return extractTwoLetterCultureFromPath(location.pathname) ?? '';
}

function getSiteSettings(): Promise<SiteSettingsViewModel> {
    const cacheKey = getSettingsCacheKey();
    const cached = settingsCache.get(cacheKey);
    if (cached) {
        return cached;
    }

    const promise = loadSiteSettings(cacheKey || null);
    settingsCache.set(cacheKey, promise);
    return promise;
}

function toRelativePath(url: string): string {
    try {
        return new URL(url).pathname;
    } catch {
        return url; // already relative
    }
}

export async function getPageUrls(): Promise<{
    checkoutInformationUrl: string,
    checkoutDeliveryUrl: string,
    checkoutPaymentUrl: string,
    checkoutReceiptUrl: string,
    termsAndConditionsUrl: string,
    privacyPolicyUrl: string,
    newsletterTermsPage: string,
    giftCardCheckoutInformationUrl: string,
    giftCardCheckoutPaymentUrl: string,
    giftCardCheckoutReceiptUrl: string,
    giftCardTermsAndConditionsUrl: string,
    giftCardPrivacyPolicyUrl: string,
    giftCardNewsletterTermsPage: string,
}> {
    const mySettings = await getSiteSettings();
    return {
        checkoutInformationUrl: toRelativePath(mySettings.checkoutInformationPage?.url ?? ''),
        checkoutDeliveryUrl: toRelativePath(mySettings.checkoutDeliveryPage?.url ?? ''),
        checkoutPaymentUrl: toRelativePath(mySettings.checkoutPaymentPage?.url ?? ''),
        checkoutReceiptUrl: toRelativePath(mySettings.checkoutReceiptPage?.url ?? ''),
        termsAndConditionsUrl: toRelativePath(mySettings.termsAndConditionsPage?.url ?? ''),
        privacyPolicyUrl: toRelativePath(mySettings.privacyPolicyPage?.url ?? ''),
        newsletterTermsPage: toRelativePath(mySettings.newsletterTermsPage?.url ?? ''),
        giftCardCheckoutInformationUrl: toRelativePath(mySettings.giftCardCheckoutInformationPage?.url ?? ''),
        giftCardCheckoutPaymentUrl: toRelativePath(mySettings.giftCardCheckoutPaymentPage?.url ?? ''),
        giftCardCheckoutReceiptUrl: toRelativePath(mySettings.giftCardCheckoutReceiptPage?.url ?? ''),
        giftCardTermsAndConditionsUrl: toRelativePath(mySettings.giftCardTermsAndConditionsPage?.url ?? ''),
        giftCardPrivacyPolicyUrl: toRelativePath(mySettings.giftCardPrivacyPolicyPage?.url ?? ''),
        giftCardNewsletterTermsPage: toRelativePath(mySettings.giftCardNewsletterTermsPage?.url ?? ''),
    };
}

export async function getCultureSettings(): Promise<{
    cultureNameForCookieBot: string,
    culture: string,
    commerceEnabled: boolean,
    isMarketGiftCardOnly: boolean,
    showFreightText: boolean,
    country: string,
    pricePostfix: string
}> {
    const cultureSettings = await getSiteSettings();
    return {
        cultureNameForCookieBot: cultureSettings.cultureNameForCookieBot || '',
        culture: cultureSettings.culture || '',
        commerceEnabled: cultureSettings.commerceEnabled || false,
        isMarketGiftCardOnly: cultureSettings.isMarketGiftCardOnly || false,
        showFreightText: cultureSettings.showFreightText || false,
        country: cultureSettings.country || '',
        pricePostfix: cultureSettings.pricePostfix || '',
    };
}

export default {
    getPage,
    get settings() {
        return getSiteSettings();
    },
};
